"use client";

import { useEffect, useRef, useState } from "react";
import {
  headerScrollService,
  type IHeaderScrollState,
} from "@/src/shared/services/headerScroll";

interface IUseHeaderScrollOptions {
  /** Off while the page has no search header, or while a mobile search locks the body. */
  enabled: boolean;
  initialBottomNavVisible: boolean;
}

/**
 * Header (`top` / `scrolled`) and bottom-nav visibility from the window scroll.
 *
 * Reads the offset at most once per frame and keeps the last one in a ref, so a scroll
 * re-renders only when the derived state actually changes (it used to re-render the
 * layout on every scroll event).
 */
export function useHeaderScroll({
  enabled,
  initialBottomNavVisible,
}: IUseHeaderScrollOptions): IHeaderScrollState {
  const [state, setState] = useState<IHeaderScrollState>({
    headerState: "top",
    bottomNavVisible: initialBottomNavVisible,
  });
  const lastY = useRef(0);

  useEffect(() => {
    if (!enabled) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const from = lastY.current;
      lastY.current = y;
      setState((prev) => headerScrollService.next(prev, from, y));
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };

    lastY.current = window.scrollY;
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);

  return state;
}
